import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { COLORS } from "../lib/colors";

export default function LoginPage() {
  const { signIn, resetPassword, sendMagicLink } = useAuth();
  // tab: "magic" (default), "password", "reset"
  const [tab, setTab] = useState("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) return setError("Enter your email.");
    setSubmitting(true);
    try {
      await sendMagicLink(email);
      setSuccess(
        `We sent a sign-in link to ${email}. Open it on this device — the link is valid for one hour.`
      );
    } catch (err) {
      const msg = err?.message || "";
      // Supabase returns this when shouldCreateUser:false and the account doesn't exist.
      if (/Signups not allowed|user not found|invalid/i.test(msg)) {
        setError(
          "We couldn't find an account for that email. Ask your platform admin to invite you, or sign in with a password if you have one."
        );
      } else {
        setError(msg || "Could not send sign-in link. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email || !password) return setError("Please fill in all fields.");
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err.message || "Login failed. Check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) return setError("Enter your email.");
    setSubmitting(true);
    try {
      await resetPassword(email);
    } finally {
      // Do not leak whether the account exists.
      setSuccess("If an account exists for that email, you'll receive a reset link shortly.");
      setSubmitting(false);
    }
  };

  const switchTab = (next) => {
    setTab(next);
    setError("");
    setSuccess("");
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    marginBottom: 6,
  };

  const primaryButtonStyle = {
    width: "100%",
    padding: "11px 0",
    borderRadius: 8,
    border: "none",
    backgroundColor: COLORS.accent,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: submitting ? "wait" : "pointer",
    opacity: submitting ? 0.7 : 1,
    transition: "opacity 0.15s",
  };

  const linkButtonStyle = {
    background: "none",
    border: "none",
    color: COLORS.accent,
    fontSize: 13,
    fontFamily: "inherit",
    cursor: "pointer",
    fontWeight: 500,
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg,
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 12,
          }}>R</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Relay
          </h1>
          <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
            Careers Admin
          </p>
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: COLORS.surface,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          padding: 28,
        }}>
          {error && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 16,
              backgroundColor: COLORS.redLight, color: COLORS.red, fontSize: 13,
            }}>{error}</div>
          )}
          {success && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 16,
              backgroundColor: COLORS.greenLight, color: COLORS.green, fontSize: 13,
            }}>{success}</div>
          )}

          {tab === "magic" && (
            <form onSubmit={handleMagicLink}>
              <p style={{
                fontSize: 13, color: COLORS.textSecondary, margin: "0 0 18px", lineHeight: 1.5,
              }}>
                Sign in with a one-time link sent to your email. No password needed.
              </p>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@university.edu" style={inputStyle} autoComplete="email" autoFocus />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Sending..." : "Send sign-in link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => switchTab("password")} style={linkButtonStyle}>
                  Use password instead
                </button>
              </div>
            </form>
          )}

          {tab === "password" && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@university.edu" style={inputStyle} autoComplete="email" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Your password" style={inputStyle} autoComplete="current-password" />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Logging in..." : "Log In"}
              </button>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14,
              }}>
                <button type="button" onClick={() => switchTab("magic")} style={linkButtonStyle}>
                  Use email link instead
                </button>
                <button type="button" onClick={() => switchTab("reset")} style={linkButtonStyle}>
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {tab === "reset" && (
            <form onSubmit={handleReset}>
              <p style={{
                fontSize: 13, color: COLORS.textSecondary, margin: "0 0 18px", lineHeight: 1.5,
              }}>
                We'll email you a link to set a new password.
              </p>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@university.edu" style={inputStyle} autoComplete="email" />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Sending..." : "Send reset link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => switchTab("magic")} style={linkButtonStyle}>
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 13, color: COLORS.textSecondary, marginTop: 20 }}>
          New to Relay?{" "}
          <a
            href="?signup"
            style={{ color: COLORS.accent, fontWeight: 600, textDecoration: "none" }}
          >
            Set up your school →
          </a>
        </p>

        <p style={{ textAlign: "center", fontSize: 11, color: COLORS.textMuted, marginTop: 8 }}>
          Powered by Relay &mdash; The AI Early Talent Engine by Careersorter
        </p>
      </div>
    </div>
  );
}
